/**
 * NodePalette.tsx
 * 左サイドバーのノードパレットコンポーネント
 * ドラッグ可能なノード一覧を表示し、キャンバスへのドロップを可能にする
 */
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { NodeKind } from './types';
import { getPaletteItems } from './nodeRegistry';
import { cn } from '@/lib/utils';
import { getNodeDisplayLabel, type FlowDisplayLanguage } from './displayLanguage';
import { NODE_CATEGORY_ORDER, NODE_CATEGORY_STYLES } from './nodeCategories';

// パレットに表示するノード一覧（showInPalette=trueのもののみ）
const paletteItems = getPaletteItems({ includeMazeOnly: true });

/**
 * ノードパレットコンポーネント
 * ユーザーはここからノードをドラッグしてキャンバスに配置する
 */
type NodePaletteProps = {
  className?: string;
  columns?: 1 | 2;
  allowedNodeKinds?: readonly NodeKind[];
  layout?: 'sidebar' | 'dock';
  hideFunctionCard?: boolean;
  includeMazeOnly?: boolean;
  largeCards?: boolean;
  onTapAdd?: (kind: NodeKind) => void;
  onPointerDrop?: (kind: NodeKind, point: { x: number; y: number }) => void;
  displayLanguage: FlowDisplayLanguage;
  showCardNames?: boolean;
  onToggleCardNames?: () => void;
};

export function NodePalette({
  className,
  columns = 1,
  allowedNodeKinds,
  layout = 'sidebar',
  hideFunctionCard = false,
  includeMazeOnly = false,
  largeCards = false,
  onTapAdd,
  onPointerDrop,
  displayLanguage,
  showCardNames = true,
  onToggleCardNames,
}: NodePaletteProps) {
  const isDock = layout === 'dock';
  const pointerDragRef = useRef<{
    pointerId: number;
    kind: NodeKind;
    startX: number;
    startY: number;
    moved: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);
  const [dragPreview, setDragPreview] = useState<{
    kind: NodeKind;
    x: number;
    y: number;
  } | null>(null);

  /**
   * ドラッグ開始時のハンドラ
   * dataTransferにノード種別を設定し、FlowEditorのonDropで受け取る
   */
  const onDragStart = (e: React.DragEvent, kind: NodeKind) => {
    e.dataTransfer.setData('application/node-kind', kind);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>, kind: NodeKind) => {
    if ((e.pointerType !== 'touch' && e.pointerType !== 'pen') || !onPointerDrop) return;
    e.currentTarget.draggable = false;
    pointerDragRef.current = {
      pointerId: e.pointerId,
      kind,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = pointerDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const deltaX = e.clientX - drag.startX;
    const deltaY = e.clientY - drag.startY;
    if (!drag.moved && Math.hypot(deltaX, deltaY) < 8) return;
    if (!drag.moved && isDock && Math.abs(deltaX) > Math.abs(deltaY)) {
      e.currentTarget.draggable = true;
      pointerDragRef.current = null;
      return;
    }
    drag.moved = true;
    e.preventDefault();
    setDragPreview({ kind: drag.kind, x: e.clientX, y: e.clientY });
  };

  const finishPointerDrag = (e: React.PointerEvent<HTMLDivElement>, cancelled = false) => {
    const drag = pointerDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    e.currentTarget.draggable = true;
    pointerDragRef.current = null;
    setDragPreview(null);
    if (!drag.moved || cancelled) return;
    suppressClickRef.current = true;
    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);
    onPointerDrop?.(drag.kind, { x: e.clientX, y: e.clientY });
  };

  const onCardClick = (kind: NodeKind) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    onTapAdd?.(kind);
  };

  return (
    <div
      data-product-tour="flow-cards"
      className={cn(
        isDock
          ? 'flow-card-palette flow-card-palette--dock'
          : 'flex w-48 flex-col gap-2 overflow-y-auto border-r border-border bg-card p-4',
        className
      )}
    >
      {isDock && onToggleCardNames ? (
        <button
          type="button"
          onClick={onToggleCardNames}
          aria-pressed={showCardNames}
          aria-label={showCardNames ? 'カード名を隠す' : 'カード名を表示'}
          title={showCardNames ? 'アイコンのみ表示' : 'カード名を表示'}
          className="flow-card-palette__toggle"
        >
          {showCardNames
            ? <ChevronDown aria-hidden="true" size={16} />
            : <ChevronUp aria-hidden="true" size={16} />}
        </button>
      ) : !isDock ? (
        <h2 className="mb-2 text-sm font-semibold text-muted-foreground">Cards</h2>
      ) : null}

      {/* ドラッグ可能なノード一覧 */}
      <div className={isDock ? 'flow-card-palette__categories' : 'space-y-4'}>
        {NODE_CATEGORY_ORDER.map((category) => {
          const items = paletteItems.filter((item) => (
            item.category === category && (!hideFunctionCard || item.kind !== 'function')
            && (includeMazeOnly || !item.mazeOnly)
            && (!allowedNodeKinds || allowedNodeKinds.includes(item.kind))
          ));
          if (items.length === 0) return null;
          return (
            <section key={category} data-node-category={category} className={isDock ? 'flow-card-palette__category' : undefined}>
            {(!isDock || showCardNames) && (
              <h3 className={`mb-1.5 text-xs font-bold text-slate-600 ${isDock ? 'flow-card-palette__category-title' : ''}`}>
                {NODE_CATEGORY_STYLES[category].label[displayLanguage]}
              </h3>
            )}
            <div className={isDock ? 'flow-card-palette__cards' : columns === 2 ? 'grid grid-cols-2 gap-2' : 'grid grid-cols-3 gap-2'}>
                {items.map((item) => (
                  <div
                    key={item.kind}
                    draggable
                    onDragStart={(e) => onDragStart(e, item.kind)}
                    onPointerDown={(e) => onPointerDown(e, item.kind)}
                    onPointerMove={onPointerMove}
                    onPointerUp={finishPointerDrag}
                    onPointerCancel={(e) => finishPointerDrag(e, true)}
                    onClick={() => onCardClick(item.kind)}
                    aria-label={getNodeDisplayLabel(item.kind, displayLanguage)}
                    title={getNodeDisplayLabel(item.kind, displayLanguage)}
                    className={`flex aspect-square min-w-0 ${isDock ? 'touch-pan-x' : 'touch-none'} cursor-grab flex-col items-center justify-center gap-2 rounded-lg border-2 p-2 text-center text-xs font-semibold transition-[transform,box-shadow] duration-150 hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 active:scale-[0.97] active:cursor-grabbing ${isDock ? 'flow-card-palette__card' : ''} ${isDock && !showCardNames ? 'is-icon-only' : ''} ${largeCards ? 'xl:gap-3 xl:p-3 xl:text-sm' : ''} ${item.color}`}
                  >
                    <img
                      src={`/images/flow-cards/${item.imageKind}.png`}
                      alt=""
                      aria-hidden="true"
                      draggable={false}
                      className={`size-8 shrink-0 object-contain ${largeCards ? 'xl:size-12' : ''}`}
                    />
                    <span className={`line-clamp-2 w-full leading-tight ${isDock && !showCardNames ? 'sr-only' : ''}`}>
                      {getNodeDisplayLabel(item.kind, displayLanguage)}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {!isDock && (
        <div className="mt-4 space-y-2 text-xs leading-relaxed text-muted-foreground">
          <p>左のカードをドラッグして置き、点から点へ線をつないでください。</p>
          <p>Start と End は最初から置いてあります（けせません）。</p>
        </div>
      )}

      {dragPreview && createPortal(
        <div
          aria-hidden="true"
          data-testid="palette-drag-preview"
          className="pointer-events-none fixed z-[150] flex size-24 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-lg border-2 border-blue-400 bg-white/95 shadow-xl"
          style={{ left: dragPreview.x, top: dragPreview.y }}
        >
          <img
            src={`/images/flow-cards/${dragPreview.kind}.png`}
            alt=""
            className="size-12 object-contain"
          />
        </div>,
        document.body,
      )}
    </div>
  );
}
